import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import "./myorder-details.css";

const Myorderdetalis = () => {
  const { id: orderIdFromParams } = useParams(); // URL params से ऑर्डर ID उठाने के लिए
  const [searchParams] = useSearchParams();

  // 1. User ID और Order ID स्टेट
  const currentUserId = localStorage.getItem("user_id") || searchParams.get("user_id") || "2";
  const activeOrderId = orderIdFromParams || searchParams.get("id") || searchParams.get("order_id");

  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 2. PHP API Call Execution
  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `https://rbuymart.rbhardwaj.com/server/api/orders/my_order.php?user_id=${currentUserId}`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch order details from server.");
        }

        const data = await response.json();

        if (data.status === "success" || Array.isArray(data) || data.orders) {
          const ordersList = Array.isArray(data) ? data : data.orders || [data];
          
          // अगर Specific Order ID है तो फ़िल्टर करें, नहीं तो सबसे लेटेस्ट ऑर्डर लें
          let matchedOrder = null;
          if (activeOrderId) {
            matchedOrder = ordersList.find(
              (item) => String(item.id || item.order_id) === String(activeOrderId)
            );
          } else {
            matchedOrder = ordersList[0]; // Default: Recent Order
          }

          if (matchedOrder) {
            setOrderData(matchedOrder);
            console.log(matchedOrder);
          } else {
            setError("Requested order details not found.");
          }
        } else {
          setError(data.message || "No order details found.");
        }
      } catch (err) {
        setError(err.message || "Network error while connecting to backend.");
      } finally {
        setLoading(false);
      }
    };

    if (currentUserId) {
      fetchOrderDetails();
    }
  }, [currentUserId, activeOrderId]);

  // Loading State UI
  if (loading) {
    return (
      <div className="myorder-detailpage" style={{ textAlign: "center", padding: "50px" }}>
        <h3>Loading Order Details...</h3>
      </div>
    );
  }

  // Error State UI
  if (error || !orderData) {
    return (
      <div className="myorder-detailpage" style={{ textAlign: "center", padding: "50px", color: "red" }}>
        <h3>{error || "Order Details Unavailable"}</h3>
      </div>
    );
  }

  // 3. Dynamic Values Extractions
  const itemsList = orderData.items || orderData.order_items || [];
  const totalItemCount = itemsList.reduce((acc, item) => acc + Number(item.quantity || 1), 0);

  const subtotal = Number(orderData.subtotal || 0);
  const gstTotal = Number(orderData.gst_total || 0);
  
  const shippingCharges = Number(orderData.shipping_charges || 0);
  const discountAmount = Number(orderData.discount || 0);
  const grandTotal = Number(orderData.grand_total || orderData.amount / 100 || 0);

  const formattedDate = orderData.created_at
    ? new Date(orderData.created_at).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "N/A";

  const orderStatus = (orderData.order_status || "pending").toLowerCase();

  return (
    <div className="myorder-detailpage">
      {/* 🟢 Header Summary */}
      <div className="order-header">
        <div>
          <strong>Order ID: #{orderData.id || orderData.order_id}</strong>
          <h4>{totalItemCount} {totalItemCount === 1 ? "item" : "items"}</h4>
        </div>
        <div>
          <strong>₹{grandTotal.toLocaleString("en-IN")}</strong>
          {discountAmount > 0 && (
            <h4 className="saved">You saved ₹{discountAmount.toLocaleString("en-IN")}</h4>
          )}
        </div>
        <div>{formattedDate}</div>
      </div>

      <h3>Items Ordered & Delivery Details</h3>

      <div className="details">
        {/* 🟢 Shipment & Order Tracker */}
        <div className="shipment">
          <p>
            <strong>Shipment Status ({orderStatus.toUpperCase()})</strong>
          </p>

          <div className="progress">
            <div className="done">
              Order Confirmed
              <br />
              <small>{formattedDate}</small>
            </div>
            <div className="line"></div>
            <div className={orderStatus === "shipped" || orderStatus === "delivered" ? "done" : "pending"}>
              Shipped
            </div>
            <div className="line"></div>
            <div className={orderStatus === "delivered" ? "done" : "pending"}>
              {orderStatus === "delivered" ? "Delivered" : "Delivery Pending"}
            </div>
          </div>

          {/* 🟢 Product List (Fetched Dynamic Database Items) */}
          {itemsList.map((product, index) => {
            const itemPrice = Number(product.price || 0);
            const itemQty = Number(product.quantity || product.qty || 1);
            const imageUrl =
              product.image_url ||
              product.primary_image ||
              "https://via.placeholder.com/100?text=Product";

            return (
              <div className="product" key={product.id || index}>
                <img
                  src={
                    imageUrl.startsWith("http")
                      ? imageUrl
                      : `https://rbuymart.rbhardwaj.com/${imageUrl}`
                  }
                  alt={product.product_name || product.name}
                />
                <div className="product-details">
                  <h4>{product.product_name || product.name}</h4>
                  <p>
                    {product.variant_name ? `Variant: ${product.variant_name} | ` : ""} 
                    Qty: {itemQty}
                  </p>
                  <p>₹{(itemPrice * itemQty).toLocaleString("en-IN")}</p>
                  <p className="status" style={{ color: "#28a745" }}>
                    Status: {orderStatus}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* 🟢 Delivery & Payment Summary Section */}
        <div className="delivery">
          <h4>Delivery Address</h4>
          <div className="delivery-address">
            <p>
              <strong>{orderData.shipping_address.name || orderData.name || "Customer Address"}</strong>
            </p>
            <p>{orderData.shipping_address.address_line1}</p>
            
            {orderData.shipping_address.address_line2 && <p>{orderData.address_line2}</p>}
            <p>
              {orderData.shipping_address.city || orderData.shipping_address.city}, {orderData.shipping_address.state || orderData.address_state} - {orderData.shipping_address.pincode || orderData.address_pincode}
            </p>
            <p>Phone: {orderData.shipping_address.phone || orderData.phone}</p>
          </div>

          <br />

          <h4>Payment Summary</h4>
          <div className="payment-details" style={{ backgroundColor: "#FCF5F3" }}>
            <ul>
              <li>
                <span>Items Subtotal</span>
                <span>₹{subtotal.toLocaleString("en-IN")}</span>
              </li>
              
              {gstTotal > 0 && (
                <li>
                  <span>GST/Taxes</span>
                  <span>₹{gstTotal.toLocaleString("en-IN")}</span>
                </li>
              )}

              {discountAmount > 0 && (
                <li className="coupon">
                  <span>Coupon Discount</span>
                  <span>-₹{discountAmount.toLocaleString("en-IN")}</span>
                </li>
              )}

              <li>
                <span>Shipping Charges</span>
                <span>{shippingCharges > 0 ? `₹${shippingCharges}` : "FREE"}</span>
              </li>

              <li className="total">
                <span>Total Amount Paid</span>
                <span>₹{grandTotal.toLocaleString("en-IN")}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Myorderdetalis;